import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { FaArrowLeft, FaExternalLinkAlt } from "react-icons/fa";
import { BsCloudUpload } from "react-icons/bs";
import { FiCheck, FiPaperclip, FiTrash2 } from "react-icons/fi";

import ActionButton from "../../../../Common/ActionButton";
import { InputField, SelectField, TextAreaField } from "../../../../Common/FormComponents";
import { filterStoreProjects } from "../../../projectFilters";
import { useSession } from "../../../../../context/SessionContext";
import { getallPurchaseOrders } from "../../../../../Redux/actions/Operationsactions/getpurchaseorders";
import {
  AddPurchaseInvoiceApi,
  UpdatePurchaseInvoiceApi,
  GetBillablePurchaseInvoiceApi,
  GetPurchaseInvoiceByIdApi,
} from "../../../../../Api/Operations/PurchaseInvoices";
import "./PurchaseInvoiceForm.css";
import "../../../AddContractorsAndVendors.css";

const TOLERANCE = 0.005;
// Raw input strings -> non-negative number (0 when empty/invalid).
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const r2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;
// Quantity that will actually be sent: clamped to what is still billable.
const effQty = (line) => r2(Math.min(num(line.BilledQty), r2(line.Max)));

// Billable GRN lines (+ this draft's own lines when editing) -> editable rows.
const buildLines = (billable, invoice) => {
  const own = {};
  (invoice?.Lines || []).forEach((l) => {
    own[l.GrnLineId] = l;
  });
  const seen = new Set();
  const rows = [];
  (billable?.Grns || []).forEach((g) =>
    (g.Lines || []).forEach((l) => {
      const o = own[l.GrnLineId];
      seen.add(l.GrnLineId);
      rows.push({
        GrnLineId: l.GrnLineId,
        GrnNumber: g.GrnNumber,
        Own: Boolean(o),
        Material: l.Material,
        Unit: l.Unit,
        PoRate: Number(l.PoRate) || 0,
        // The draft's own quantity is not "already invoiced" for itself.
        Max: Number(l.RemainingQuantity) + (o ? Number(o.BilledQuantity) : 0),
        BilledQty: o ? String(o.BilledQuantity) : !invoice ? String(l.RemainingQuantity) : "",
        BilledRate: o ? String(o.BilledRate) : String(l.PoRate),
      });
    }),
  );
  // A line this draft fully consumes no longer shows up as billable.
  (invoice?.Lines || []).forEach((o) => {
    if (!seen.has(o.GrnLineId)) {
      rows.push({
        GrnLineId: o.GrnLineId,
        GrnNumber: o.GrnNumber,
        Own: true,
        Material: o.Material,
        Unit: o.Unit,
        PoRate: Number(o.PoRate) || 0,
        Max: Number(o.BilledQuantity),
        BilledQty: String(o.BilledQuantity),
        BilledRate: String(o.BilledRate),
      });
    }
  });
  return rows;
};

const PurchaseInvoiceForm = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { session, rolesdata } = useSession();

  const fileInputRef = useRef(null);
  const editId = location.state?.Id || location.state?.InvoiceId || "";
  const isEdit = Boolean(editId);

  // Redux data
  const allPOs = useSelector((state) => state.getAllPurchaseOrders?.data || []);
  const projects = useMemo(() => filterStoreProjects(rolesdata?.data?.Projects) || [], [rolesdata]);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPo, setIsLoadingPo] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [taxMode, setTaxMode] = useState("percent"); // "percent" or "amount"
  const [taxInput, setTaxInput] = useState("");
  const [invoice, setInvoice] = useState(null); // loaded draft (edit mode)
  const [selectedPoId, setSelectedPoId] = useState("");
  const [vendor, setVendor] = useState({ id: "", name: "" }); // taken from the PO
  const [grnNo, setGrnNo] = useState(""); // GRN whose billable lines are shown
  const [lines, setLines] = useState([]); // every billable row of the PO

  const [formData, setFormData] = useState({
    ProjectId: session?.ProjectId ? String(session.ProjectId) : "",
    InvoiceDate: moment().format("YYYY-MM-DD"),
    VendorInvoiceNo: "",
    DueDate: "",
    AdjustmentNotes: "",
    FreightAmount: "",
  });

  useEffect(() => {
    if (!isEdit && !formData.ProjectId && projects.length > 0) {
      setFormData((prev) => ({ ...prev, ProjectId: String(projects[0].ProjectId) }));
    }
  }, [isEdit, formData.ProjectId, projects]);

  useEffect(() => {
    if (formData.ProjectId) {
      dispatch(getallPurchaseOrders(formData.ProjectId));
    }
  }, [dispatch, formData.ProjectId]);

  // Edit mode: load the draft and fill the header.
  useEffect(() => {
    if (!isEdit) {
      return undefined;
    }
    let cancelled = false;
    GetPurchaseInvoiceByIdApi(editId).then((res) => {
      if (cancelled) {
        return;
      }
      const inv = res?.data?.Data;
      if (!res?.data?.IsSuccess || !inv) {
        toast.error(res?.data?.Message || "Failed to load purchase invoice");
        history.push("/procurement/purchase-invoice");
        return;
      }
      if (inv.Status !== "Draft") {
        toast.error("Only a draft invoice can be edited");
        history.push("/procurement/purchase-invoice");
        return;
      }
      setFormData({
        ProjectId: String(inv.ProjectId),
        InvoiceDate: moment(inv.InvoiceDate).format("YYYY-MM-DD"),
        VendorInvoiceNo: inv.VendorInvoiceNo || "",
        DueDate: inv.DueDate ? moment(inv.DueDate).format("YYYY-MM-DD") : "",
        AdjustmentNotes: inv.Narration || "",
        FreightAmount: Number(inv.FreightAmount) ? String(inv.FreightAmount) : "",
      });
      // Stored value is an amount, so show it as one.
      setTaxMode("amount");
      setTaxInput(Number(inv.TaxAmount) ? String(inv.TaxAmount) : "");
      setGrnNo(inv.Lines?.[0]?.GrnNumber || "");
      setInvoice(inv);
      setSelectedPoId(String(inv.PurchaseOrderId));
    });
    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, history]);

  // Load what is still billable for the chosen PO.
  useEffect(() => {
    if (!selectedPoId) {
      setLines([]);
      setVendor({ id: "", name: "" });
      setIsLoadingPo(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoadingPo(true);
    GetBillablePurchaseInvoiceApi(selectedPoId).then((res) => {
      if (cancelled) {
        return;
      }
      setIsLoadingPo(false);
      if (res?.data?.IsSuccess) {
        setVendor({
          id: String(res.data.Data?.VendorId || ""),
          name: res.data.Data?.VendorName || "",
        });
        setLines(buildLines(res.data.Data, invoice));
      } else {
        setVendor({ id: "", name: "" });
        setLines([]);
        toast.error(res?.data?.Message || "Failed to load billable GRN lines");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedPoId, invoice]);

  // Dropdown options
  const vendorOptions = useMemo(
    () => [
      { value: "", label: "Select Vendor" },
      ...(vendor.id ? [{ value: vendor.id, label: vendor.name }] : []),
    ],
    [vendor],
  );

  const projectOptions = useMemo(
    () => [
      { value: "", label: "Select Project" },
      ...projects.map((p) => ({ value: String(p.ProjectId), label: p.ProjectName })),
    ],
    [projects],
  );

  const poOptions = useMemo(() => {
    const list = (allPOs || [])
      .filter((po) => String(po.ProjectId) === String(formData.ProjectId))
      .map((po) => ({
        value: String(po.Id),
        label: `${po.PoNumber}${po.VendorName ? ` - ${po.VendorName}` : ""}`,
      }));
    // Edit mode: the PO list may not have loaded yet, so label it from the invoice.
    if (invoice && !list.some((o) => o.value === String(invoice.PurchaseOrderId))) {
      list.push({ value: String(invoice.PurchaseOrderId), label: invoice.PoNumber });
    }
    return [{ value: "", label: "Select Purchase Order" }, ...list];
  }, [allPOs, formData.ProjectId, invoice]);

  const grnOptions = useMemo(
    () => [
      { value: "", label: "Select GRN" },
      ...[...new Set(lines.map((l) => l.GrnNumber))].map((n) => ({ value: n, label: n })),
    ],
    [lines],
  );

  const handleChange = (e) => {
    const { name, id, value } = e.target;
    const fieldName = name || id;

    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    if (fieldName === "ProjectId") {
      setSelectedPoId("");
      setGrnNo("");
    }
  };

  // Raw strings are kept while typing; clamping happens in effQty / at submit.
  const handleLineChange = (grnLineId, field, value) => {
    setLines((prev) => prev.map((l) => (l.GrnLineId === grnLineId ? { ...l, [field]: value } : l)));
  };

  // Rows of the chosen GRN, plus this draft's own rows so editing never drops them.
  const visibleLines = useMemo(
    () => lines.filter((l) => l.Own || l.GrnNumber === grnNo),
    [lines, grnNo],
  );

  // Calculations
  const itemsSubtotal = useMemo(
    () => visibleLines.reduce((acc, l) => acc + r2(effQty(l) * r2(num(l.BilledRate))), 0),
    [visibleLines],
  );
  const freightAmount = r2(num(formData.FreightAmount));
  const taxAmount =
    taxMode === "percent" ? r2((itemsSubtotal * num(taxInput)) / 100) : r2(num(taxInput));
  const grandTotal = r2(itemsSubtotal + freightAmount + taxAmount);

  // Billed lines only: honest 3-way check of billed rate vs PO rate and billed qty vs remaining.
  const enteredLines = visibleLines.filter((l) => num(l.BilledQty) > 0);
  const billedLines = enteredLines.filter((l) => effQty(l) > 0);
  const isMatchVerified = enteredLines.every(
    (l) =>
      Math.abs(num(l.BilledRate) - l.PoRate) <= TOLERANCE && num(l.BilledQty) <= l.Max + TOLERANCE,
  );

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachedFile(file);
      toast.info(`Attached: ${file.name}`);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!selectedPoId) {
      toast.error("Linked Purchase Order is required");
      return;
    }
    if (!formData.VendorInvoiceNo.trim()) {
      toast.error("Vendor Invoice / Bill No. is required");
      return;
    }
    if (!formData.InvoiceDate) {
      toast.error("Invoice Date is required");
      return;
    }
    const over = enteredLines.find((l) => num(l.BilledQty) > l.Max + TOLERANCE);
    if (over) {
      toast.error(
        `Billed quantity for ${over.Material} (${over.GrnNumber}) exceeds the remaining ${r2(over.Max)}`,
      );
      return;
    }
    if (billedLines.length === 0) {
      toast.error("Select a GRN and enter a billed quantity for at least one line");
      return;
    }
    const badRate = billedLines.some((l) => {
      const r = parseFloat(l.BilledRate);
      return !Number.isFinite(r) || r < 0;
    });
    if (badRate) {
      toast.error("Enter a valid billed rate for every billed line");
      return;
    }

    const payload = new FormData();
    payload.append("PurchaseOrderId", selectedPoId);
    payload.append("VendorInvoiceNo", formData.VendorInvoiceNo.trim());
    payload.append("InvoiceDate", formData.InvoiceDate);
    if (formData.DueDate) {
      payload.append("DueDate", formData.DueDate);
    }
    payload.append("FreightAmount", String(freightAmount));
    payload.append("TaxAmount", String(taxAmount));
    if (formData.AdjustmentNotes.trim()) {
      payload.append("Narration", formData.AdjustmentNotes.trim());
    }
    billedLines.forEach((line, index) => {
      payload.append(`Lines[${index}].GrnLineId`, line.GrnLineId);
      payload.append(`Lines[${index}].BilledQuantity`, String(effQty(line)));
      payload.append(`Lines[${index}].BilledRate`, String(r2(parseFloat(line.BilledRate))));
    });
    if (attachedFile) {
      payload.append("File", attachedFile);
    }

    setIsLoading(true);
    try {
      const res = isEdit
        ? await UpdatePurchaseInvoiceApi(payload, editId)
        : await AddPurchaseInvoiceApi(payload);

      if (res?.data?.IsSuccess) {
        toast.success(res.data.Message || "Purchase Invoice saved successfully!");
        history.push("/procurement/purchase-invoice");
      } else {
        toast.error(res?.data?.Message || "Failed to save purchase invoice");
      }
    } catch (err) {
      toast.error("Error saving purchase invoice");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="purchase-invoice-screen-wrapper">
      {/* Header bar */}
      <div className="col-md-12 text-left page_heading mb-3">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <FaArrowLeft
              size={16}
              style={{ cursor: "pointer", marginRight: "12px" }}
              title="Back"
              onClick={() => history.goBack()}
            />
            <h2 className="grn-header-title">{isEdit ? "Edit" : "Create"} Purchase Invoice</h2>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} autoComplete="off">
        {/* Top 2 Cards: Billing & Reference Details (Left) + 3-Way Match Verification (Right) */}
        <div className="row">
          {/* CARD 1: Billing & Reference Details */}
          <div className="col-lg-6 col-12 mb-3">
            <div className="purchase-invoice-card h-100">
              <div className="purchase-invoice-card-header">
                <h5 className="purchase-invoice-card-title">Billing & Reference Details</h5>
              </div>

              {/* Row 1: Invoice Number & Project */}
              <div className="row mb-2">
                <InputField
                  label="Invoice / Bill Number:"
                  name="InvoiceNumber"
                  value={invoice?.InvoiceNumber || "Assigned on save"}
                  disabled
                  stacked
                  wrapperCol="col-lg-6 col-md-6 col-12"
                />
                <SelectField
                  label="Project:"
                  name="ProjectId"
                  value={formData.ProjectId}
                  onChange={handleChange}
                  options={projectOptions}
                  disabled={isEdit}
                  required
                  stacked
                  wrapperCol="col-lg-6 col-md-6 col-12"
                />
                <SelectField
                  label="Vendor:"
                  name="VendorId"
                  value={vendor.id}
                  options={vendorOptions}
                  disabled
                  required
                  stacked
                  wrapperCol="col-lg-6 col-md-6 col-12"
                />
                <InputField
                  label="Invoice Date:"
                  name="InvoiceDate"
                  type="date"
                  value={formData.InvoiceDate}
                  onChange={handleChange}
                  required
                  stacked
                  wrapperCol="col-lg-6 col-md-6 col-12"
                />

                <SelectField
                  label="Linked Purchase Order:"
                  name="LinkedPoId"
                  value={selectedPoId}
                  onChange={(e) => {
                    setSelectedPoId(e.target.value);
                    setGrnNo("");
                  }}
                  options={poOptions}
                  disabled={isEdit}
                  required
                  stacked
                  wrapperCol="col-lg-6 col-md-6 col-12"
                />
                <SelectField
                  label="Linked Gate Receipt (GRN):"
                  name="LinkedGrnId"
                  value={grnNo}
                  onChange={(e) => setGrnNo(e.target.value)}
                  options={grnOptions}
                  required={!isEdit}
                  stacked
                  wrapperCol="col-lg-6 col-md-6 col-12"
                >
                  <div className="field-helper-link ml-md-3">
                    <span>Link</span>
                    <FaExternalLinkAlt size={10} />
                    <span className="text-muted ml-1" style={{ fontSize: "11px" }}>
                      (Shows matched link)
                    </span>
                  </div>
                </SelectField>

                <InputField
                  label="Vendor Invoice / Bill No.:"
                  name="VendorInvoiceNo"
                  value={formData.VendorInvoiceNo}
                  onChange={handleChange}
                  required
                  stacked
                  wrapperCol="col-lg-6 col-md-6 col-12"
                >
                  <div className="field-helper-note ml-md-3">(Invoice issued by the vendor)</div>
                </InputField>
                <InputField
                  label="Due Date:"
                  name="DueDate"
                  type="date"
                  value={formData.DueDate}
                  onChange={handleChange}
                  required
                  stacked
                  wrapperCol="col-lg-6 col-md-6 col-12"
                />
              </div>
            </div>
          </div>

          {/* CARD 2: 3-Way Match Verification */}
          <div className="col-lg-6 col-12 mb-3">
            <div className="purchase-invoice-card h-100">
              <div className="purchase-invoice-card-header">
                <div>
                  <h5 className="purchase-invoice-card-title d-inline-block">
                    3-Way Match Verification
                  </h5>
                  <span className="purchase-invoice-card-subtitle">(Items & Quantities)</span>
                </div>
                {enteredLines.length > 0 && (
                  <span className={`match-verified-badge ${isMatchVerified ? "" : "warning"}`}>
                    {isMatchVerified ? (
                      <>
                        <FiCheck size={14} /> 3-Way Match Verified
                      </>
                    ) : (
                      "⚠️ Rate / Qty Variance Detected"
                    )}
                  </span>
                )}
              </div>

              <div className="table-responsive">
                <table className="invoice-items-table">
                  <thead>
                    <tr>
                      <th style={{ width: "24%" }}>Material</th>
                      <th style={{ width: "14%" }}>Packaging/Unit</th>
                      <th style={{ width: "9%" }} className="text-center">
                        Remaining Qty
                      </th>
                      <th style={{ width: "12%" }} className="text-center">
                        Billed Qty <span className="text-danger">*</span>
                      </th>
                      <th style={{ width: "13%" }} className="text-right">
                        PO Rate
                      </th>
                      <th style={{ width: "13%" }} className="text-right">
                        Billed Rate <span className="text-danger">*</span>
                      </th>
                      <th style={{ width: "7%" }} className="text-center">
                        Variance
                      </th>
                      <th style={{ width: "18%" }} className="text-right">
                        Line Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingPo ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="text-center py-3"
                          style={{ color: "#64748b", fontSize: "13px" }}
                        >
                          <span className="mr-2">⏳</span> Loading billable GRN lines…
                        </td>
                      </tr>
                    ) : visibleLines.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="text-center py-3"
                          style={{ color: "#94a3b8", fontSize: "13px" }}
                        >
                          {selectedPoId
                            ? "Select a GRN with billable lines to load its items."
                            : "Select a Purchase Order above to load line items for verification."}
                        </td>
                      </tr>
                    ) : (
                      visibleLines.map((line) => {
                        const billedRate = r2(num(line.BilledRate));
                        const poRate = line.PoRate;
                        const variance = r2(billedRate - poRate);
                        const lineTotal = r2(effQty(line) * billedRate);

                        return (
                          <tr key={line.GrnLineId}>
                            <td style={{ fontWeight: 600, color: "#1e293b" }}>{line.Material}</td>
                            <td style={{ color: "#64748b" }}>{line.Unit}</td>
                            <td className="text-center" style={{ fontWeight: 500 }}>
                              {r2(line.Max)}
                            </td>
                            <td className="text-center">
                              <input
                                type="number"
                                className="invoice-table-input text-center"
                                value={line.BilledQty}
                                onChange={(e) =>
                                  handleLineChange(line.GrnLineId, "BilledQty", e.target.value)
                                }
                                step="any"
                                min="0"
                                placeholder="0"
                              />
                            </td>
                            <td className="text-right" style={{ color: "#475569" }}>
                              {poRate.toLocaleString()}
                            </td>
                            <td className="text-right">
                              <input
                                type="number"
                                className="invoice-table-input text-right"
                                value={line.BilledRate}
                                onChange={(e) =>
                                  handleLineChange(line.GrnLineId, "BilledRate", e.target.value)
                                }
                                step="any"
                                min="0"
                              />
                            </td>
                            <td
                              className="text-center"
                              style={{
                                fontWeight: 600,
                                color:
                                  variance === 0 ? "#64748b" : variance > 0 ? "#dc2626" : "#16a34a",
                              }}
                            >
                              {variance}
                            </td>
                            <td
                              className="text-right"
                              style={{ fontWeight: 700, color: "#0f172a" }}
                            >
                              {lineTotal.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CARD 3: Commercial Summary & Attachments */}
        <div className="purchase-invoice-card">
          <div className="purchase-invoice-card-header">
            <h5 className="purchase-invoice-card-title">Commercial Summary & Attachments</h5>
          </div>

          <div className="row">
            {/* Notes and attachment in one responsive two-column row */}
            <div className="col-lg-6 col-12 pr-lg-4">
              <div className="row mx-0">
                <TextAreaField
                  label="Tax & Commercial Adjustment Notes:"
                  name="AdjustmentNotes"
                  value={formData.AdjustmentNotes}
                  onChange={handleChange}
                  placeholder="Enter adjustment notes..."
                  rows={4}
                  stacked
                  wrapperCol="col-lg-6 col-md-6 col-12 pr-md-2"
                />

                <div className="col-lg-6 col-md-6 col-12 pl-md-2 mb-3 stacked-form-field">
                  <label className="input_label d-block m-0 mb-2">
                    Attach Vendor Invoice (Scanned/Photo)
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    hidden
                    onChange={handleFileUpload}
                    accept="image/*,.pdf"
                  />
                  {!attachedFile && invoice?.File && (
                    <div className="mb-2" style={{ fontSize: "13px" }}>
                      <a href={invoice.File} target="_blank" rel="noopener noreferrer">
                        {invoice.FileName || "Current attachment"}
                      </a>
                    </div>
                  )}
                  {attachedFile ? (
                    <div className="document-upload-box justify-content-start">
                      <FiPaperclip className="document-upload-icon" />

                      <div className="document-upload-file text-truncate">
                        <span className="text-truncate">{attachedFile.name}</span>

                        <button
                          type="button"

                          className="document-upload-remove"

                          onClick={(e) => {
                            e.stopPropagation();

                            setAttachedFile(null);

                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}

                          title="Remove attachment"
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="document-upload-box"

                      role="button"

                      tabIndex={0}

                      onClick={() => fileInputRef.current && fileInputRef.current.click()}

                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();

                          fileInputRef.current && fileInputRef.current.click();
                        }
                      }}
                    >
                      <BsCloudUpload className="document-upload-icon" />
                      <div className="document-upload-copy">
                        <strong>Upload invoice</strong>
                        <span>Image or PDF</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right side: Financial Summary Box */}
            <div className="col-lg-6 col-12 mt-4 mt-lg-0 pl-lg-4">
              <div className="invoice-summary-box">
                {/* Items Subtotal */}
                <div className="invoice-summary-row">
                  <span className="invoice-summary-label">Items Subtotal</span>
                  <span className="invoice-summary-val">Rs. {itemsSubtotal.toLocaleString()}</span>
                </div>

                {/* Freight / Delivery */}
                <div className="invoice-summary-row">
                  <span className="invoice-summary-label">Freight / Delivery</span>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    className="invoice-summary-input"
                    name="FreightAmount"
                    value={formData.FreightAmount}
                    onChange={handleChange}
                    placeholder="0"
                  />
                </div>

                {/* Sales Tax / GST */}
                <div className="invoice-summary-row">
                  <span className="invoice-summary-label mr-2">Sales Tax / GST</span>
                  <div className="d-flex align-items-center">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      className="invoice-summary-input"
                      name="TaxInput"
                      value={taxInput}
                      onChange={(e) => setTaxInput(e.target.value)}
                      placeholder="0"
                    />
                    <select
                      className="ml-2 border rounded py-1 px-2"
                      style={{ fontSize: "12px", outline: "none", backgroundColor: "#fff" }}
                      value={taxMode}
                      onChange={(e) => {
                        setTaxMode(e.target.value);
                        setTaxInput("");
                      }}
                    >
                      <option value="percent">%</option>
                      <option value="amount">PKR</option>
                    </select>
                  </div>
                </div>
                {taxMode === "percent" && (
                  <div className="text-muted text-right mb-2" style={{ fontSize: "11.5px" }}>
                    Tax amount: Rs. {taxAmount.toLocaleString()}
                  </div>
                )}

                {/* Grand Total Billed */}
                <div className="invoice-grand-total-row">
                  <span className="invoice-grand-total-label">Grand Total Billed</span>
                  <span className="invoice-grand-total-val">Rs. {grandTotal.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="col-12 p-0 mt-3 mb-5">
          <div className="float-right">
            <ActionButton
              text="Cancel"
              variant="cancel"
              type="button"
              onClick={() => history.goBack()}
              disabled={isLoading}
            />
            <ActionButton
              text="Save Purchase Invoice"
              icon={BsCloudUpload}
              type="submit"
              disabled={isLoading || isLoadingPo}
              isLoading={isLoading}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default PurchaseInvoiceForm;
